import { Box, Button, Chip, Stack, TextField } from '@mui/material'
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setInvoiceEmailTo } from 'store/invoices/invoices.slice'

const EMAIL_DOMAIN_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/

interface InvoiceToEmailProps {
  defaultEmail?: string
  errorOnSending?: boolean
  userEmail?: string | null
  factoringEmails?: string[]
}

const InvoiceToEmail = ({
  defaultEmail,
  errorOnSending,
  userEmail,
  factoringEmails = [],
}: InvoiceToEmailProps) => {
  const [emails, setEmails] = useState<string[]>([])
  const [value, setValue] = useState('')

  const dispatch = useDispatch()

  const existingEmails = useMemo(() => {
    const all = [...emails]
    if (userEmail) {
      all.push(userEmail)
    }
    all.push(...factoringEmails)
    return all.map((email) => email.toLowerCase())
  }, [emails, userEmail, factoringEmails])

  const getInputError = (input: string): string | null => {
    const trimmed = input.trim()
    if (!trimmed) {
      return null
    }

    if (existingEmails.includes(trimmed.toLowerCase())) {
      return 'This email address has already been added.'
    }

    return null
  }

  const isEmailValid = (input: string) => {
    const trimmed = input.trim()
    return trimmed.length > 0 && EMAIL_DOMAIN_REGEX.test(trimmed)
  }

  const inputError = getInputError(value)
  const showAddButton = value.trim().length > 0
  const canAddEmail = isEmailValid(value) && !inputError

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value)
  }

  const handleAddEmail = () => {
    const newEmail = value.trim()
    const error = getInputError(newEmail)

    if (!newEmail || error || !isEmailValid(newEmail)) {
      return
    }

    setEmails((prevEmails) => [...prevEmails, newEmail])
    setValue('')
  }

  const handleInputKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAddEmail()
    }
  }

  const handleDeleteEmail = (index: number) => {
    const filteredEmails = emails.filter((_, i) => i !== index)
    setEmails(filteredEmails)
  }

  useEffect(() => {
    if (!!defaultEmail && defaultEmail !== '') {
      // Don't add defaultEmail if it's a factoring email, user email, or already in the list
      const isFactoringEmail = factoringEmails.includes(defaultEmail)
      const isUserEmail = userEmail === defaultEmail
      if (!emails.includes(defaultEmail) && !isFactoringEmail && !isUserEmail) {
        setEmails([...emails, defaultEmail])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail, factoringEmails, userEmail])

  useEffect(() => {
    // Only send editable emails to backend - factoring emails are handled by backend automatically
    if (!!emails) {
      dispatch(setInvoiceEmailTo(emails.join(',')))
    }
  }, [dispatch, emails])

  return (
    <Box
      sx={{
        borderBottom: '0.8px solid #dedede',
        padding: emails.length > 0 || factoringEmails.length > 0 || userEmail ? 2 : 0,
        paddingLeft: 2,
        paddingRight: 2,
      }}
    >
      <Stack direction={'row'} flexWrap={'wrap'} mb={1}>
        {/* Display user email as read-only (non-deletable, display-only - backend adds it automatically) */}
        {userEmail && (
          <Chip
            key={`user-${userEmail}`}
            label={userEmail}
            sx={{
              marginBottom: 1,
              marginLeft: 1,
              backgroundColor: '#9E9E9E', // Gray background
              color: '#FFFFFF', // White text
              '&:hover': {
                backgroundColor: '#9E9E9E', // Keep same color on hover (read-only)
              },
            }}
            onDelete={undefined}
          />
        )}
        {/* Display factoring emails as read-only (non-deletable, display-only - not sent to backend) */}
        {/* Backend automatically adds these emails as CC when factoring is enabled */}
        {factoringEmails.map((email) => (
          <Chip
            key={`factoring-${email}`}
            label={email}
            sx={{
              marginBottom: 1,
              marginLeft: 1,
              backgroundColor: '#9E9E9E', // Gray background
              color: '#FFFFFF', // White text
              '&:hover': {
                backgroundColor: '#9E9E9E', // Keep same color on hover (read-only)
              },
            }}
            onDelete={undefined}
          />
        ))}
        {/* Display editable emails - these are sent to backend */}
        {emails.map((email, index) => (
          <Chip
            key={email}
            label={email}
            color="primary"
            sx={{ marginBottom: 1, marginLeft: 1 }}
            onDelete={() => handleDeleteEmail(index)}
          />
        ))}
      </Stack>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <TextField
          fullWidth
          variant="standard"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyPress}
          placeholder="Add email addresses..."
          size="small"
          error={!!inputError || errorOnSending}
          helperText={inputError ?? (errorOnSending ? 'Please enter a valid email address.' : '')}
          sx={{
            '& .MuiInputBase-root:before': {
              borderBottom: 'none',
            },
            '& .MuiInputBase-root:after': {
              borderBottom: 'none',
            },
            '& .MuiInputBase-root:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottom: 'none',
            },
          }}
        />
        {showAddButton && (
          <Button
            variant="contained"
            disabled={!canAddEmail}
            onClick={handleAddEmail}
            sx={{
              borderRadius: '6px',
              textTransform: 'capitalize',
              fontWeight: 700,
              bgcolor: 'primary.main',
              minWidth: '110px',
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            Add Email
          </Button>
        )}
      </Stack>
    </Box>
  )
}

export default InvoiceToEmail
